import * as React from 'react'
import { useMemo } from 'react'
import { Button, Flex, Tag, Typography } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { useUnit } from 'effector-react'
import { useSearchParams } from 'react-router-dom'

import { $countryNameByCode } from '../../../../features/dictionaries/model'
import { FLOW_FILTER_KEYS } from '../VerificationsListFilters/config'

const { Text } = Typography

export type ActiveFlowFilters = {
  status?: string[]
  country?: string[]
  hasDeposit?: boolean
  isDuplicate?: boolean
}

type Props = {
  filters: ActiveFlowFilters
  onResetAll: () => void
}

export const ActiveFiltersDisplay: React.FC<Props> = ({ filters, onResetAll }) => {
  const countryNameByCode = useUnit($countryNameByCode)
  const [searchParams] = useSearchParams()

  const hasMeaningfulFilters = useMemo(
    () => FLOW_FILTER_KEYS.some((key) => searchParams.has(key)),
    [searchParams],
  )

  const tags = useMemo(() => {
    const items: { key: string; label: string }[] = []

    if (filters.status?.length) {
      items.push({
        key: 'status',
        label: `Status: ${filters.status.join(', ')}`,
      })
    }
    if (filters.country?.length) {
      const names = filters.country.map(
        (code) => countryNameByCode[code] || code,
      )
      items.push({
        key: 'country',
        label: `Country: ${names.join(', ')}`,
      })
    }
    if (filters.hasDeposit !== undefined) {
      items.push({
        key: 'hasDeposit',
        label: `Deposit: ${filters.hasDeposit ? 'Yes' : 'No'}`,
      })
    }
    if (filters.isDuplicate !== undefined) {
      items.push({
        key: 'isDuplicate',
        label: `Duplicate: ${filters.isDuplicate ? 'Yes' : 'No'}`,
      })
    }

    return items
  }, [filters, countryNameByCode])

  if (!hasMeaningfulFilters || tags.length === 0) {
    return null
  }

  return (
    <div
      style={{
        borderRadius: 8,
        padding: '8px 14px',
        marginBottom: 12,
        border: '1px solid var(--crm-border, #d9d9d9)',
        background: 'var(--crm-bg, #fff)',
      }}
    >
      <Flex gap={6} wrap="wrap" align="center">
        <Text
          type="secondary"
          style={{
            fontSize: 10,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            whiteSpace: 'nowrap',
          }}
        >
          Active filters:
        </Text>
        {tags.map((t) => (
          <Tag
            key={t.key}
            color="blue"
            style={{ fontSize: 11, margin: 0 }}
          >
            {t.label}
          </Tag>
        ))}
        <Button
          size="small"
          type="text"
          icon={<ReloadOutlined />}
          onClick={onResetAll}
          style={{ color: '#ef4444', fontSize: 11 }}
        >
          Reset all
        </Button>
      </Flex>
    </div>
  )
}
